import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import SweetAlert from 'react-bootstrap-sweetalert';
import ReactTooltip from 'react-tooltip'
import classNames from 'classnames';
import moment from "moment";
import { useSnackbar } from "notistack";
import { Col } from "reactstrap";

import { Row } from "components/Layout";
import Image from "components/Image";
import NTMButton from "components/Buttons/NTMButton";
import NTMSelect from "components/Buttons/NTMSelect";
import NTMTable from "components/Table/NTMTable";
import PageWithModal from "components/Page/PageWithModal";
import PhenologyPeriodModal from "components/Modal/PhenologyPeriodModal";
import PhenologyAgendaModal from "components/Modal/PhenologyAgendaModal";

import { AGRISMART, get, put, post, deleteElem, getErrorMessage } from "service/Client";
import { useGetDevices } from "service/AgrismartService";

import { crops as CROPS, roles } from '../../constants'
import { alertIcon_tri, editIcon, editIcon_over, deleteIcon, deleteIcon_over, addIcon, addIcon_over } from 'img/icons'
import style from './_agrismart.module.scss'


const AlertSignal = message => <div>
    <div data-tip={message}>
        <img alt="alert" src={alertIcon_tri} style={{ width: "20px", height: "20px" }} />
    </div>
    <ReactTooltip effect='solid' />
</div>


export default function PhenologyPage() {
    let { crop } = useParams()
    let { devices } = useGetDevices()
    const project = useSelector(store => store.session.project)
    const user = useSelector(store => store.users.data)

    const [projectAgenda, setProjectAgenda] = useState([])
    const [selectedAgenda, setSelectedAgenda] = useState()
    const [currentAgenda, setCurrentAgenda] = useState([])
    const [validAgenda, setValidAgenda] = useState(true)
    const [activePhase, setActivePhase] = useState()
    const [agendaInfo, setAgendaInfo] = useState()
    const [agendaAction, setAgendaAction] = useState()
    const [periodModalOpen, setPeriodModalOpen] = useState()
    const [alert, setAlert] = useState(null);

    const { enqueueSnackbar } = useSnackbar()

    const analyzeAgenda = useCallback(agenda => {
        let valid = true
        agenda.forEach((a, i, arr) => {
            let startDate = moment(a.startDate, 'YYYY-MM-DD')
            let endDate = moment(a.endDate, 'YYYY-MM-DD')
            let isActive = moment().isBetween(startDate, endDate, 'day', '[]')
            let err = { notConfirmed: false, invalidStartDate: false, invalidEndDate: false }
            let canModify = false

            if (i > 0 && !a.confirmed && startDate.isBefore(moment())) err.notConfirmed = true
            if (i > 0 && !startDate.isAfter(moment(arr[i - 1].endDate, 'YYYY-MM-DD'))) err.invalidStartDate = true
            if (!endDate.isAfter(startDate)) err.invalidEndDate = true

            // imposto il flag di modifica
            // per abilitare la modifica si deve verificare: ultima fase confermata o prima fase non confermata
            if (a.confirmed) {
                if (i < arr.length - 1) canModify = arr[i + 1].confirmed ? false : true
                else canModify = true
            }
            else { // not confirmed
                if (i === 0) canModify = true
                else canModify = arr[i - 1].confirmed ? true : false
            }

            arr[i] = { ...a, isActive, canModify, err }
            if (err.invalidStartDate || err.invalidEndDate) valid = false
        })
        setValidAgenda(valid)
    }, [])

    function getProjectAgenda() {
        if (project) {
            get(`${AGRISMART}/phenology/agenda?project=${project.id}`).then(resp => {
                let projectAgenda = resp.filter(a => a.cropName === crop).sort((a, b) => b.creationDate - a.creationDate)
                setProjectAgenda(projectAgenda)
                if (projectAgenda && projectAgenda.length > 0) setSelectedAgenda(projectAgenda[0].id)
            })
        }
    }

    function extractActivePhase() {
        let activePhase = currentAgenda.find(a =>
            moment().isBetween(moment(a.startDate, 'YYYY-MM-DD'), moment(a.endDate, 'YYYY-MM-DD'), 'day', '[]'))
        setActivePhase(activePhase ? activePhase.phenologicalPhase.name : null)
    }

    function extractCurrentAgenda() {
        if (!selectedAgenda) return
        let selection = projectAgenda.find(a => a.id === selectedAgenda)
        if (!selection) return
        let agenda = selection.periods.map(a => ({ ...a }))
            .sort((a, b) => a.phenologicalPhase.ordinal - b.phenologicalPhase.ordinal)
        analyzeAgenda(agenda)
        setCurrentAgenda(agenda)
    }

    useEffect(getProjectAgenda, [project, crop])
    useEffect(extractCurrentAgenda, [projectAgenda, selectedAgenda, analyzeAgenda])
    useEffect(extractActivePhase, [currentAgenda])

    function closeAlert() {
        setAlert(null);
    }

    function openConfirm() {
        setAlert(<SweetAlert success title={"Salvato correttamente"} onConfirm={closeAlert} onCancel={closeAlert} />);
    }

    function showError(e) {
        enqueueSnackbar(getErrorMessage(e), { variant: "error" })
    }


    function saveCurrentAgenda() {
        put(`${AGRISMART}/phenology/period`, { body: currentAgenda })
            .then(() => {
                openConfirm()
                getProjectAgenda()
            })
            .catch(showError)
    }

    function setStartDate(id, startDate, confirmed, cascade) {
        setPeriodModalOpen(null)
        let index = currentAgenda.findIndex(p => p.id === id)
        if (index === -1 || !startDate) return
        let newAgenda = [...currentAgenda]
        newAgenda[index].confirmed = confirmed
        let dayShift = moment(startDate).diff(moment(newAgenda[index].startDate, 'YYYY-MM-DD'), 'days')
        if (dayShift) {
            for (let i = index; i < newAgenda.length; i++) {
                if (i > index && !cascade) break;
                newAgenda[i].startDate = moment(newAgenda[i].startDate, 'YYYY-MM-DD').add(dayShift, 'day').format('YYYY-MM-DD')
                if (i > 0) {
                    newAgenda[i - 1].endDate = moment(newAgenda[i].startDate, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
                }
            }
        }
        analyzeAgenda(newAgenda)
        setCurrentAgenda(newAgenda)
    }

    function deleteAgenda(a) {
        setAlert(<SweetAlert
            warning
            showCancel
            cancelBtnText={"Annulla"}
            confirmBtnText="Ho capito, elimina"
            confirmBtnBsStyle="danger"
            title="Confermi?"
            onConfirm={() => {
                deleteElem(`${AGRISMART}/phenology/agenda/${a.id}`)
                    .then(() => { getProjectAgenda() })
                    .catch(showError)
                    .finally(() => setAlert(null))
            }}
            onCancel={() => setAlert(null)}
            focusCancelBtn
        >
            {`L'agenda '${a.name}' sarà eliminata`}
        </SweetAlert>)
    }

    function submitAgendaInfo(a) {
        switch (agendaAction) {
            case 'create':
                post(`${AGRISMART}/phenology/agenda`, { body: { ...a, projectId: project.id, cropName: crop } })
                    .then(() => { getProjectAgenda() })
                    .catch(showError)
                break
            case 'clonate':
                post(`${AGRISMART}/phenology/agenda?fromAgendaId=${a.id}`, { body: a })
                    .then(() => { getProjectAgenda() })
                    .catch(showError)
                break
            case 'update':
                put(`${AGRISMART}/phenology/agenda/${a.id}`, { body: a })
                    .then(() => { getProjectAgenda() })
                    .catch(showError)
                break
            default:
                break
        }
        setAgendaAction(null)
    }


    let periodTabHeader = [
        {
            Header: "Fase fenologica",
            accessor: 'phaseName',
            Cell: props => <div className={classNames(style.phenologyLabel)} active={props.original.isActive ? 'true' : undefined} >
                {props.original.phenologicalPhase.label}
            </div>
        },
        {
            Header: "Data inizio",
            accessor: 'startDate',
            Cell: props => <div>
                {moment(props.original.startDate, 'YYYY-MM-DD').format("DD/MM/YYYY")}
                {props.original.err.invalidStartDate && AlertSignal("La data di inizio deve seguire la data di fine della fase precedente")}
            </div>
        },
        {
            Header: "Data fine",
            accessor: 'endDate',
            Cell: props => <div>
                {moment(props.original.endDate, 'YYYY-MM-DD').format("DD/MM/YYYY")}
                {props.original.err.invalidEndDate && AlertSignal("La data di fine deve seguire la data di inizio")}
            </div>
        },
        {
            Header: "Confermata",
            accessor: 'confirmed',
            Cell: props => <div>
                {props.original.confirmed ? 'SÌ' : 'NO'}
                {props.original.err.notConfirmed && AlertSignal("Data inizio fase fenologica non confermata")}
            </div>
        },
    ]

    let agendaTabHeader = [
        {
            Header: "Nome",
            accessor: 'name',
        },
        {
            Header: "Data creazione",
            accessor: 'creationDate',
            Cell: props => moment(props.original.creationDate).format("DD/MM/YYYY HH:mm")
        },
        {
            Header: "Anno",
            accessor: 'year',
        },
        {
            Header: "Dispositivi asociati",
            accessor: 'devices',
            Cell: props => props.original?.devices?.length > 0 ? (
                props.original.devices.map(d => <div key={d}>{d}</div>)
            ) : (
                AlertSignal("Nessun dispositivo associato")
            )
        },
        {
            Header: "Inizio",
            accessor: 'startDate',
            Cell: props => moment(props.original.startDate, 'YYYY-MM-DD').format("DD/MM/YYYY")
        },
        {
            Header: "Fine",
            accessor: 'endDate',
            Cell: props => moment(props.original.endDate, 'YYYY-MM-DD').format("DD/MM/YYYY")
        },
        {
            Header: "Cultivar",
            accessor: 'cultivar',
        },
        {
            Header: "Rinnovo automatico",
            accessor: 'renew',
            Cell: props => <span>{props.original.renew ? 'SÌ' : 'NO'}</span>
        },
    ]

    if (roles.isAdmin(user)) {
        periodTabHeader.push({
            Header: "Modifica",
            accessor: 'modify',
            Cell: props => <Row style={{ justifyContent: 'center' }}>
                {props.original.canModify &&
                    <div data-tip="Modifica data di inizio">
                        <Image
                            src={editIcon}
                            hover={editIcon_over}
                            onClick={() => setPeriodModalOpen(props.original)}
                        />
                        <ReactTooltip effect='solid' />
                    </div>
                }
            </Row>
        })
        agendaTabHeader.push({
            Header: "Modifica",
            accessor: 'modify',
            Cell: props => <Row style={{ justifyContent: 'center' }}>
                <div data-tip="Modifica calendario">
                    <Image
                        src={editIcon}
                        hover={editIcon_over}
                        onClick={() => { setAgendaAction('update'); setAgendaInfo({ ...props.original, periods: null }) }}
                    />
                    <ReactTooltip effect='solid' />
                </div>
                &nbsp;
                <div data-tip="Clona calendario">
                    <Image
                        src={addIcon}
                        hover={addIcon_over}
                        onClick={() => { setAgendaAction('clonate'); setAgendaInfo({ ...props.original, periods: null }) }}
                    />
                    <ReactTooltip effect='solid' />
                </div>
                &nbsp;
                <div data-tip="Elimina calendario">
                    <Image
                        src={deleteIcon}
                        hover={deleteIcon_over}
                        onClick={() => deleteAgenda(props.original)}
                    />
                    <ReactTooltip effect='solid' />
                </div>
            </Row>
        })
    }

    return (
        <PageWithModal>
            <Row>
                <NTMSelect
                    value={selectedAgenda}
                    onChange={event => setSelectedAgenda(parseInt(event.target.value))}
                >
                    {projectAgenda.map(a =>
                        <option key={a.id} value={a.id}>{a.name || a.year}</option>
                    )}
                </NTMSelect>
            </Row>
            <Row className={classNames(style.phenologyContainer)}>
                {CROPS.phases[crop].map(phase =>
                    <Col key={phase.name}
                        className={classNames(style.phenologyItem)}
                        active={phase.name === activePhase ? 'true' : undefined}
                    >
                        <label>{phase.label}</label>
                        <picture>
                            <img src={phase.picture} alt={phase.name} />
                        </picture>
                        <label>{phase.period}</label>
                    </Col>
                )}
            </Row>
            <Row>
                <NTMTable
                    title="Periodi fenologici"
                    header={periodTabHeader}
                    body={currentAgenda}
                    defaultPageSize={10}
                    showPagination={true}
                />
            </Row>
            {currentAgenda && roles.isAdmin(user) &&
                <Row style={{ paddingLeft: "1rem", marginBottom: "2rem" }}>
                    <NTMButton primary onClick={extractCurrentAgenda}>Annulla</NTMButton>
                    <NTMButton primary disabled={!validAgenda} onClick={saveCurrentAgenda}>
                        Salva
                        {!validAgenda && AlertSignal("Vi sono alcuni errori")}
                    </NTMButton>
                </Row>
            }
            <Row>
                <NTMTable
                    title="Calendari fenologici"
                    header={agendaTabHeader}
                    body={projectAgenda}
                    defaultPageSize={5}
                    showPagination={true}
                />
            </Row>
            {roles.isAdmin(user) &&
                <Row style={{ paddingLeft: "1rem" }}>
                    <NTMButton primary onClick={() => {
                        setAgendaInfo(null)
                        setAgendaAction('create')
                    }}>Nuovo calendario</NTMButton>
                </Row>
            }

            <PhenologyPeriodModal
                isOpen={!!periodModalOpen}
                onClose={() => setPeriodModalOpen(null)}
                onSubmit={setStartDate}
                item={periodModalOpen || {}}
            />
            <PhenologyAgendaModal
                isOpen={!!agendaAction}
                onClose={() => setAgendaAction(null)}
                onSubmit={submitAgendaInfo}
                agenda={agendaInfo}
                setAgenda={setAgendaInfo}
                allDevices={devices}
            />
            {alert}
        </PageWithModal>
    );
}
